'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-connect/email.mdx
 * (published at user-docs.totalaccess.co.za/total-connect/email)
 */

/**
 * FolderManager - Component for managing custom email folders
 *
 * Features:
 * - View standard system folders
 * - Create custom folders
 * - Rename custom folders
 * - Delete custom folders
 * - Set folder colors and icons
 * - View shared mailbox folders
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FolderOpen,
  FolderPlus,
  Trash2,
  Edit2,
  Plus,
  Loader2,
  Check,
  X,
  ChevronRight,
  ChevronDown,
  Inbox,
  Send,
  FileText,
  Archive,
  Star,
  AlertCircle,
  Ban,
  Clock,
  Calendar,
  Palette,
  Mail,
  Users,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { cn } from '@/lib/utils';
import { notify } from '@/lib/notifications';
import emailApiService from '../services/emailApi.service';
import type { Folder } from '../types';

// System folder icons
const SYSTEM_FOLDER_ICONS: Record<string, React.ElementType> = {
  'INBOX': Inbox,
  'Sent': Send,
  'Drafts': FileText,
  'Trash': Trash2,
  'Archive': Archive,
  'Starred': Star,
  'Spam': AlertCircle,
  'Junk': Ban,
  'Outbox': Clock,
  'Scheduled': Calendar,
};

// Predefined colors for custom folders
const FOLDER_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Teal', value: '#14b8a6' },
];

interface DelegatedAccount {
  mailboxId: number;
  emailAddress: string;
  displayName: string;
  isShared: boolean;
  permissions: {
    canRead: boolean;
    canSend: boolean;
    canSendAs: boolean;
    canSendOnBehalf: boolean;
    canManage: boolean;
  };
  unreadCount?: number;
}

interface FolderManagerProps {
  className?: string;
  /** Callback when folders are created, updated, or deleted - parent can refresh folder list */
  onFoldersChanged?: () => void;
  /** Delegated/shared mailbox accounts */
  delegatedAccounts?: DelegatedAccount[];
}

export default function FolderManager({ className, onFoldersChanged, delegatedAccounts = [] }: FolderManagerProps) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [customFolders, setCustomFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState(FOLDER_COLORS[0].value);
  // Track which mailbox to create folder for (null = personal, number = shared mailbox id)
  const [newFolderMailboxId, setNewFolderMailboxId] = useState<number | null>(null);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['system', 'custom', 'shared']));
  const [expandedMailboxes, setExpandedMailboxes] = useState<Set<number>>(new Set());
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null);
  // Track which mailbox a folder being edited/deleted belongs to (null = personal)
  const [editingFolderMailboxId, setEditingFolderMailboxId] = useState<number | null>(null);
  const [folderToDeleteMailboxId, setFolderToDeleteMailboxId] = useState<number | null>(null);
  // Custom folders per shared mailbox
  const [sharedMailboxCustomFolders, setSharedMailboxCustomFolders] = useState<Record<number, Folder[]>>({});
  // All folders per shared mailbox (system + custom)
  const [sharedMailboxAllFolders, setSharedMailboxAllFolders] = useState<Record<number, Folder[]>>({});

  // Load folders for personal mailbox
  const loadFolders = useCallback(async () => {
    try {
      setLoading(true);

      const foldersRes = await emailApiService.getFolders();

      if (foldersRes.success && foldersRes.data) {
        const allFolders = Array.isArray(foldersRes.data) ? foldersRes.data : [];
        setFolders(allFolders);

        // Filter out custom folders (non-system folders)
        const custom = allFolders.filter(f => !f.isSystem && f.type === 'custom');
        setCustomFolders(custom);
      } else {
        setFolders([]);
        setCustomFolders([]);
      }

      // Also load custom folders for each shared mailbox
      const sharedFolders: Record<number, Folder[]> = {};
      const sharedAllFolders: Record<number, Folder[]> = {};
      for (const account of delegatedAccounts) {
        try {
          const sharedRes = await emailApiService.getFolders(account.mailboxId);
          if (sharedRes.success && sharedRes.data) {
            const allSharedFolders = Array.isArray(sharedRes.data) ? sharedRes.data : [];
            sharedAllFolders[account.mailboxId] = allSharedFolders;
            sharedFolders[account.mailboxId] = allSharedFolders.filter(f => !f.isSystem && f.type === 'custom');
          }
        } catch (err) {
          console.error(`Failed to load folders for mailbox ${account.mailboxId}:`, err);
        }
      }
      setSharedMailboxCustomFolders(sharedFolders);
      setSharedMailboxAllFolders(sharedAllFolders);
    } catch (error: any) {
      console.error('Error loading folders:', error);
      notify.error('Failed to load folders');
    } finally {
      setLoading(false);
    }
  }, [delegatedAccounts]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  // Create new folder (supports personal and shared mailboxes)
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      notify.warning('Please enter a folder name');
      return;
    }

    try {
      setSaving(true);

      const response = await emailApiService.createFolder(
        newFolderName.trim(),
        undefined,
        {
          color: newFolderColor,
          mailboxId: newFolderMailboxId ?? undefined
        }
      );

      if (response.success) {
        const mailboxName = newFolderMailboxId
          ? delegatedAccounts.find(a => a.mailboxId === newFolderMailboxId)?.displayName || 'shared mailbox'
          : 'personal mailbox';
        notify.success(`Folder created in ${mailboxName}`);
        setShowNewFolder(false);
        setNewFolderName('');
        setNewFolderColor(FOLDER_COLORS[0].value);
        setNewFolderMailboxId(null);
        // Reload folder list
        await loadFolders();
        // Notify parent to refresh global folder list
        onFoldersChanged?.();
        // Dispatch global event so page.tsx can refresh navigation
        window.dispatchEvent(new CustomEvent('emailFoldersChanged', {
          detail: { mailboxId: newFolderMailboxId }
        }));
      } else {
        notify.error(response.error || 'Failed to create folder');
      }
    } catch (error: any) {
      console.error('Error creating folder:', error);
      notify.error(error.message || 'Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  // Update folder
  const handleUpdateFolder = async () => {
    if (!editingFolder || !editName.trim()) return;

    try {
      setSaving(true);

      const response = await emailApiService.updateFolder(
        editingFolder.path,
        {
          name: editName.trim(),
          color: editColor,
          mailboxId: editingFolderMailboxId ?? undefined
        }
      );

      if (response.success) {
        notify.success('Folder updated successfully');
        setEditingFolder(null);
        setEditingFolderMailboxId(null);
        loadFolders();
        // Notify parent to refresh global folder list
        onFoldersChanged?.();
        // Dispatch global event so page.tsx can refresh navigation
        window.dispatchEvent(new CustomEvent('emailFoldersChanged', {
          detail: { mailboxId: editingFolderMailboxId }
        }));
      } else {
        notify.error(response.error || 'Failed to update folder');
      }
    } catch (error: any) {
      console.error('Error updating folder:', error);
      notify.error(error.message || 'Failed to update folder');
    } finally {
      setSaving(false);
    }
  };

  // Confirm delete
  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;

    const folder = folderToDelete;
    const mailboxId = folderToDeleteMailboxId;
    setFolderToDelete(null);
    setFolderToDeleteMailboxId(null);

    try {
      setSaving(true);

      const response = await emailApiService.deleteFolder(folder.path, mailboxId ?? undefined);

      if (response.success) {
        notify.success('Folder deleted');
        loadFolders();
        // Notify parent to refresh global folder list
        onFoldersChanged?.();
        // Dispatch global event so page.tsx can refresh navigation
        window.dispatchEvent(new CustomEvent('emailFoldersChanged', {
          detail: { mailboxId }
        }));
      } else {
        notify.error(response.error || 'Failed to delete folder');
      }
    } catch (error: any) {
      console.error('Error deleting folder:', error);
      notify.error(error.message || 'Failed to delete folder');
    } finally {
      setSaving(false);
    }
  };

  // Toggle section
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Toggle individual shared mailbox expansion
  const toggleMailbox = (mailboxId: number) => {
    setExpandedMailboxes(prev => {
      const next = new Set(prev);
      if (next.has(mailboxId)) {
        next.delete(mailboxId);
      } else {
        next.add(mailboxId);
      }
      return next;
    });
  };

  // Get folder icon
  const getFolderIcon = (folder: Folder) => {
    const Icon = SYSTEM_FOLDER_ICONS[folder.name] || FolderOpen;
    return Icon;
  };

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-12", className)}>
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-500">Loading folders...</span>
      </div>
    );
  }

  // Separate system and custom folders
  const systemFolderNames = Object.keys(SYSTEM_FOLDER_ICONS);
  const systemFolders = folders.filter(f => 
    systemFolderNames.some(name => f.name.toLowerCase() === name.toLowerCase())
  );

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Email Folders
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage your email folders and create custom folders to organize your inbox
          </p>
        </div>
        <button
          onClick={() => setShowNewFolder(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <FolderPlus className="w-4 h-4" />
          New Folder
        </button>
      </div>

      {/* New Folder Form */}
      {showNewFolder && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Create New Folder
          </h4>
          <div className="space-y-3">
            {/* Mailbox selector - only show if there are shared mailboxes with manage permission */}
            {delegatedAccounts.some(a => a.permissions.canManage) && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400 min-w-[60px]">Mailbox:</span>
                <select
                  value={newFolderMailboxId ?? ''}
                  onChange={(e) => setNewFolderMailboxId(e.target.value ? Number(e.target.value) : null)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Personal Mailbox</option>
                  {delegatedAccounts.filter(a => a.permissions.canManage).map(account => (
                    <option key={account.mailboxId} value={account.mailboxId}>
                      {account.displayName || account.emailAddress} (Shared)
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Color:</span>
                <div className="flex gap-1">
                  {FOLDER_COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setNewFolderColor(color.value)}
                      className={cn(
                        "w-6 h-6 rounded-full transition-transform",
                        newFolderColor === color.value && "ring-2 ring-offset-2 ring-gray-400 scale-110"
                      )}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateFolder}
                  disabled={saving || !newFolderName.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowNewFolder(false);
                    setNewFolderName('');
                    setNewFolderMailboxId(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Folders Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => toggleSection('system')}
          className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-2">
            {expandedSections.has('system') ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
            <span className="font-medium text-gray-900 dark:text-white">System Folders</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
              {systemFolders.length}
            </span>
          </div>
        </button>
        
        {expandedSections.has('system') && (
          <div className="px-4 pb-4">
            <div className="space-y-1">
              {systemFolders.map((folder) => {
                const Icon = getFolderIcon(folder);
                return (
                  <div
                    key={folder.id}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/30"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-white">{folder.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      {folder.unreadCount !== undefined && folder.unreadCount > 0 && (
                        <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full">
                          {folder.unreadCount}
                        </span>
                      )}
                      {folder.totalCount !== undefined && (
                        <span>{folder.totalCount} emails</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              System folders cannot be renamed or deleted
            </p>
          </div>
        )}
      </div>

      {/* Custom Folders Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => toggleSection('custom')}
          className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <div className="flex items-center gap-2">
            {expandedSections.has('custom') ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
            <span className="font-medium text-gray-900 dark:text-white">Custom Folders</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
              {customFolders.length}
            </span>
          </div>
        </button>
        
        {expandedSections.has('custom') && (
          <div className="px-4 pb-4">
            {customFolders.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  No custom folders yet
                </p>
                <button
                  onClick={() => setShowNewFolder(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Create your first folder
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                {customFolders.map((folder) => (
                  <div
                    key={folder.id}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/30 group"
                  >
                    {editingFolder?.id === folder.id ? (
                      <div className="flex items-center gap-3 flex-1">
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: editColor || folder.color || '#6b7280' }}
                        />
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          {FOLDER_COLORS.map((color) => (
                            <button
                              key={color.value}
                              onClick={() => setEditColor(color.value)}
                              className={cn(
                                "w-5 h-5 rounded-full transition-transform",
                                editColor === color.value && "ring-2 ring-offset-1 ring-gray-400 scale-110"
                              )}
                              style={{ backgroundColor: color.value }}
                            />
                          ))}
                        </div>
                        <button
                          onClick={handleUpdateFolder}
                          disabled={saving}
                          className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingFolder(null)}
                          className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: folder.color || '#6b7280' }}
                          />
                          <span className="text-sm text-gray-900 dark:text-white">{folder.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {folder.totalCount !== undefined && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {folder.totalCount} emails
                            </span>
                          )}
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setEditingFolder(folder);
                                setEditingFolderMailboxId(null);
                                setEditName(folder.name);
                                setEditColor(folder.color || FOLDER_COLORS[0].value);
                              }}
                              className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title="Rename folder"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setFolderToDelete(folder);
                                setFolderToDeleteMailboxId(null);
                              }}
                              className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                              title="Delete folder"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Shared Mailbox Folders Section */}
      {delegatedAccounts.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => toggleSection('shared')}
            className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <div className="flex items-center gap-2">
              {expandedSections.has('shared') ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
              <Users className="w-4 h-4 text-purple-500" />
              <span className="font-medium text-gray-900 dark:text-white">Shared Mailbox Folders</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                {delegatedAccounts.length} mailbox{delegatedAccounts.length !== 1 ? 'es' : ''}
              </span>
            </div>
          </button>

          {expandedSections.has('shared') && (
            <div className="px-4 pb-4 space-y-3">
              {delegatedAccounts.map((account) => (
                <div key={account.mailboxId} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                  {/* Mailbox Header */}
                  <button
                    onClick={() => toggleMailbox(account.mailboxId)}
                    className="flex items-center justify-between w-full px-3 py-2 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30"
                  >
                    <div className="flex items-center gap-2">
                      {expandedMailboxes.has(account.mailboxId) ? (
                        <ChevronDown className="w-4 h-4 text-purple-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-purple-500" />
                      )}
                      <Mail className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {account.displayName || account.emailAddress}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {account.unreadCount !== undefined && account.unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {account.unreadCount}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {account.permissions.canManage ? 'Full access' : 'Read access'}
                      </span>
                    </div>
                  </button>

                  {/* Mailbox Folders */}
                  {expandedMailboxes.has(account.mailboxId) && (
                    <div className="px-3 py-2 space-y-1 bg-white dark:bg-gray-800">
                      {/* System folders from API (fallback to hardcoded if not loaded) */}
                      {(sharedMailboxAllFolders[account.mailboxId] || [])
                        .filter(f => f.isSystem)
                        .map((folder) => {
                          const Icon = getFolderIcon(folder);
                          return (
                            <div
                              key={`${account.mailboxId}-sys-${folder.id}`}
                              className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/30"
                            >
                              <div className="flex items-center gap-3">
                                <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                <span className="text-sm text-gray-900 dark:text-white">{folder.displayName || folder.name}</span>
                              </div>
                              {folder.unreadCount !== undefined && folder.unreadCount > 0 && (
                                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  {folder.unreadCount}
                                </span>
                              )}
                            </div>
                          );
                        })}

                      {/* Custom folders for this shared mailbox */}
                      {sharedMailboxCustomFolders[account.mailboxId]?.length > 0 && (
                        <>
                          <div className="border-t border-gray-200 dark:border-gray-600 my-2" />
                          <p className="text-xs text-gray-500 dark:text-gray-400 pl-3 mb-1">Custom Folders</p>
                          {sharedMailboxCustomFolders[account.mailboxId].map((folder) => (
                            <div
                              key={`${account.mailboxId}-custom-${folder.id}`}
                              className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/30 group"
                            >
                              {editingFolder?.id === folder.id && editingFolderMailboxId === account.mailboxId ? (
                                <div className="flex items-center gap-3 flex-1">
                                  <div
                                    className="w-4 h-4 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: editColor || folder.color || '#6b7280' }}
                                  />
                                  <input
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
                                    autoFocus
                                  />
                                  <div className="flex gap-1">
                                    {FOLDER_COLORS.map((color) => (
                                      <button
                                        key={color.value}
                                        onClick={() => setEditColor(color.value)}
                                        className={cn(
                                          "w-5 h-5 rounded-full transition-transform",
                                          editColor === color.value && "ring-2 ring-offset-1 ring-gray-400 scale-110"
                                        )}
                                        style={{ backgroundColor: color.value }}
                                      />
                                    ))}
                                  </div>
                                  <button
                                    onClick={handleUpdateFolder}
                                    disabled={saving}
                                    className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => { setEditingFolder(null); setEditingFolderMailboxId(null); }}
                                    className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-3">
                                    <div
                                      className="w-4 h-4 rounded-full"
                                      style={{ backgroundColor: folder.color || '#6b7280' }}
                                    />
                                    <span className="text-sm text-gray-900 dark:text-white">{folder.displayName || folder.name}</span>
                                  </div>
                                  {account.permissions.canManage && (
                                    <div className="flex items-center gap-2">
                                      {folder.totalCount !== undefined && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {folder.totalCount} emails
                                        </span>
                                      )}
                                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          onClick={() => {
                                            setEditingFolder(folder);
                                            setEditingFolderMailboxId(account.mailboxId);
                                            setEditName(folder.displayName || folder.name);
                                            setEditColor(folder.color || FOLDER_COLORS[0].value);
                                          }}
                                          className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                          title="Rename folder"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => {
                                            setFolderToDelete(folder);
                                            setFolderToDeleteMailboxId(account.mailboxId);
                                          }}
                                          className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                          title="Delete folder"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                        </>
                      )}

                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 pl-3">
                        {account.permissions.canManage
                          ? 'You have full management access to this mailbox. Use "New Folder" to create folders.'
                          : 'You have read-only access to this mailbox'}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Shared mailbox folders are managed by your administrator. Contact them to request access or folder changes.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {folderToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete Folder
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <span className="font-medium">"{folderToDelete.name}"</span>? 
              Emails in this folder will be moved to Trash. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setFolderToDelete(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteFolder}
                disabled={saving}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
